import { Alert } from '@designsforhealth/dfh-react-components'
import { PortableTextBlock } from '@portabletext/types'
import { bind, Subscribe } from '@react-rxjs/core'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import withDispensaryAccount, {
  WithDispensaryAccountFC,
} from '../../../../../../hoc/withDispensaryAccount'
import { useLogAndCaptureLeftResult } from '../../../../../../hooks/useLogAndCaptureLeftResult'
import { useResultWithLeftEffect } from '../../../../../../hooks/useResultWithLeftEffect'
import {
  setUpdateDispensaryInput,
  useSanityDispensaryGetOrCreate,
  useSanityDispensaryUpdate,
} from '../../../../../../lib/sanity-dispensaries/manage/dispensary'
import { dispensariesSiteSettingsCdn$ } from '../../../../../../lib/sanity-dispensaries/query'
import { constant, constEmptyResult, constUndefined } from '../../../../../../utils/constant'
import { mapRight } from '../../../../../../utils/rx/operators'
import { EmptyObject } from '../../../../../../utils/types'
import ContactSupportMessage from '../../../../../ContactSupportMessage'
import { DfhSiteLogo } from '../../../../../global/SiteLogo'
import Spinner from '../../../../../Spinner'
import ImageEditor from '../ImageEditor'
import {
  ImagePreview as DefaultImagePreview,
  ImagePreviewContainer as DefaultImagePreviewContainer,
} from '../ImageEditor/styled'
import TextEditor from '../TextEditor'

const StyledHeading = styled.h4`
  margin-top: 30px;
`

const StyledImagePreviewContainer = styled(DefaultImagePreviewContainer)`
  background-color: ${({ theme }) => theme.colors.component.backgroundDarkBlue};
  // improve display in small viewport when wrapped
  min-height: 110px;
`

const StyledImagePreview = styled(DefaultImagePreview)`
  height: 110px;
`

const StyledDfhLogo = styled.img`
  max-width: 270px;
`

const StyledContainer = styled.div`
  margin-bottom: 2rem;
  @media (min-width: 1100px) {
    margin-bottom: 0;
  }
`

const DefaultLogoPreview: React.VFC = () => (
  <DfhSiteLogo alt="Default logo" component={StyledDfhLogo} />
)

const pluckData = <T,>({ data }: { data: T }) => data

const [useDefaultDispensaryBio] = bind(
  dispensariesSiteSettingsCdn$.pipe(
    mapRight((siteSettingsResult) => ({
      _tag: 'Right' as const,
      data: siteSettingsResult.data?.defaultDispensaryBio,
    })),
  ),
)

const constEmptyBlocks = constant([] as PortableTextBlock[])

const CustomizeLogAndBioReactive: React.FC = () => {
  const [sanityDispensaryLeft, sanityDispensary] = useResultWithLeftEffect(
    useSanityDispensaryGetOrCreate(),
    pluckData,
    constUndefined,
  )

  const [, defaultBioBlocks] = useResultWithLeftEffect(
    useDefaultDispensaryBio(),
    pluckData,
    constEmptyBlocks,
  )

  const { loading: updateLoading, result: updateResult } = useSanityDispensaryUpdate()
  useLogAndCaptureLeftResult(updateResult ?? constEmptyResult())

  const [{ updateSource, previousDispensary }, setUpdateState] = useState<{
    updateSource: 'logo' | 'bioImage' | 'bioContent' | undefined
    previousDispensary: typeof sanityDispensary
  }>({ updateSource: undefined, previousDispensary: undefined })

  const handleLogoSubmit = useCallback(
    (assetRef: string) => {
      setUpdateDispensaryInput(
        assetRef
          ? { method: 'PUT', input: { logoRef: assetRef } }
          : { method: 'DELETE', input: { attrs: ['logo'] } },
      )
      setUpdateState(() => ({ updateSource: 'logo', previousDispensary: sanityDispensary }))
    },
    [sanityDispensary],
  )

  const handleBioImageSubmit = useCallback(
    (assetRef: string) => {
      setUpdateDispensaryInput(
        assetRef
          ? { method: 'PUT', input: { bioImageRef: assetRef } }
          : { method: 'DELETE', input: { attrs: ['bio.image'] } },
      )
      setUpdateState(() => ({ updateSource: 'bioImage', previousDispensary: sanityDispensary }))
    },
    [sanityDispensary],
  )

  const handleBioContentSubmit = useCallback(
    (blocks: PortableTextBlock[]) => {
      setUpdateDispensaryInput(
        blocks
          ? { method: 'PUT', input: { bioContent: blocks } }
          : { method: 'DELETE', input: { attrs: ['bio.content'] } },
      )
      setUpdateState(() => ({ updateSource: 'bioContent', previousDispensary: sanityDispensary }))
    },
    [sanityDispensary],
  )

  if (sanityDispensaryLeft ?? !sanityDispensary) {
    return (
      <div>
        <Alert type="error">
          An error occurred retrieving your dispensary customization, and we have been notified.{' '}
          <ContactSupportMessage />
        </Alert>
      </div>
    )
  }

  // detect when update succeeded, but dispensary still refers to previous value
  const isSubmitted = updateResult?._tag === 'Right' && sanityDispensary === previousDispensary

  return (
    <StyledContainer>
      <h3>Customize Logo and Bio</h3>
      {updateResult?._tag === 'Left' && (
        <Alert type="error">
          An error occurred updating your dispensary, and we have been notified.{' '}
          <ContactSupportMessage />
        </Alert>
      )}
      <StyledHeading>Edit Logo</StyledHeading>
      <ImageEditor
        sanityValue={sanityDispensary.logo?.image?.asset._ref}
        handleSubmit={handleLogoSubmit}
        loading={updateLoading && updateSource === 'logo'}
        submitDisabled={updateLoading || isSubmitted}
        aspectRatio="logo"
        defaultImagePreviewComponent={DefaultLogoPreview}
        imagePreviewComponent={StyledImagePreview}
        previewContainerComponent={StyledImagePreviewContainer}
      />
      <StyledHeading>Edit Bio Image</StyledHeading>
      <ImageEditor
        sanityValue={sanityDispensary.bio?.image?.asset._ref}
        handleSubmit={handleBioImageSubmit}
        loading={updateLoading && updateSource === 'bioImage'}
        submitDisabled={updateLoading || isSubmitted}
      />
      <StyledHeading>Edit Bio</StyledHeading>
      <TextEditor
        sanityValue={sanityDispensary.bio?.content}
        defaultValue={defaultBioBlocks}
        handleSubmit={handleBioContentSubmit}
        loading={updateLoading && updateSource === 'bioContent'}
        submitDisabled={updateLoading || isSubmitted}
      />
    </StyledContainer>
  )
}

const CustomizeLogoAndBio: WithDispensaryAccountFC<EmptyObject> = () => (
  <Subscribe fallback={<Spinner loading />}>
    <CustomizeLogAndBioReactive />
  </Subscribe>
)

export default withDispensaryAccount(CustomizeLogoAndBio)
